/**
 * Apps store — the user's hosted apps across tasks, one row per pool port.
 *
 * Fed by ONE level-triggered event (apps:changed carries the whole list every
 * time) plus one seed fetch after a reload. The store therefore only ever
 * REPLACES its list; nothing patches a row in place, so it cannot drift from
 * the server's task_apps rows, which are the truth.
 *
 * "resting" is not stored: it is derived where it is rendered (a running row
 * while the sandbox is asleep), from the presence beat.
 */
import { create } from "zustand";
import type { TaskApp } from "@bash/shared";

interface AppsStore {
  apps: TaskApp[];
  /** True once a seed read or an apps:changed has delivered a real list. */
  loaded: boolean;
  /** Bumps on every replace; a seed that started before a replace is stale. */
  version: number;
  replace: (apps: TaskApp[]) => void;
  /** Sign-out / socket teardown: the next account must not see this list. */
  reset: () => void;
}

export const useAppsStore = create<AppsStore>((set) => ({
  apps: [],
  loaded: false,
  version: 0,
  replace: (apps) =>
    set((s) => ({ apps, loaded: true, version: s.version + 1 })),
  // Monotonic on purpose: a seed captured before a sign-out must read the
  // post-reset store as CHANGED, or it would write the old account's list back.
  reset: () =>
    set((s) => ({ apps: [], loaded: false, version: s.version + 1 })),
}));
